import { useState } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { Logo } from '@/components/logos/logo';

function useSocialConnections() {
  const { user } = useUser();
  const { toast } = useToast();
  const [state, setState] = useState({
    isRemoving: false,
    activeProvider: undefined as string | undefined
  });

  const disconnectSocialAccount = async (provider: string) => {
    setState((prev) => ({ ...prev, isRemoving: true, activeProvider: provider }));
    try {
      const account = user?.externalAccounts.find((acc) => acc.provider === provider);
      await account?.destroy();
      await user?.reload();
      toast({
        title: 'Account disconnected',
        description: `Your ${provider} account has been disconnected.`,
        variant: 'default'
      });
    } catch (error) {
      const typedError = error as { errors: { longMessage: string | null }[] };
      toast({
        title: 'Error',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    } finally {
      setState((prev) => ({ ...prev, isRemoving: false, activeProvider: undefined }));
    }
  };

  return {
    ...state,
    disconnectSocialAccount,
    connectedAccounts: user?.externalAccounts ?? []
  };
}

export function SocialConnections() {
  const { connectedAccounts, disconnectSocialAccount, isRemoving, activeProvider } =
    useSocialConnections();

  return (
    <div className="space-y-4">
      <CardTitle className="text-lg">Social Accounts</CardTitle>
      <div className="bg-card/50 border rounded-lg p-4">
        <div className="space-y-2">
          {connectedAccounts.length > 0 ? (
            connectedAccounts.map((account) => (
              <div
                key={account.id}
                className="flex justify-between items-center py-2.5 border-b last:border-b-0"
              >
                <div className="flex items-center gap-3">
                  <Logo icon="google" width={20} height={20} />
                  <div>
                    <div className="text-sm">{account.emailAddress}</div>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={isRemoving && activeProvider === account.provider}
                    >
                      <EllipsisVertical className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive-foreground cursor-pointer"
                      onClick={() => {
                        void disconnectSocialAccount(account.provider);
                      }}
                    >
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground">No social accounts connected.</p>
          )}
        </div>
      </div>
    </div>
  );
}
